"use client";

import { Link } from "@/i18n/navigation";
import {
  PhoneIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import Contact from "@/components/Contact/Contact";

const PHONE_TEL = "+32493966507";

const CONTENT = {
  fr: {
    h1: "Comment se passe le rachat d'objets lors d'un vide maison à Bruxelles ?",
    heroText:
      "Lors d'un vide maison à Bruxelles, certains meubles, bijoux ou objets anciens peuvent avoir une valeur réelle : plutôt que de les évacuer avec le reste, nous les examinons et déduisons leur montant directement de votre devis. Voici comment cette estimation se déroule concrètement, du premier contact à la déduction finale, sans démarche séparée ni obligation d'accepter quoi que ce soit.",
    callNow: "Appeler maintenant",
    ctaEstimate: "Faire estimer mes objets",
    freeQuote: "Demander un devis gratuit",
    s1: {
      title: "Quels objets sont concernés par un rachat ?",
      p1: "Un bien occupé depuis plusieurs décennies contient souvent des pièces qu'on ne pense plus à valoriser : un secrétaire remisé dans une chambre d'ami, un tiroir de bijoux, une collection de livres ou de vinyles jamais réévaluée. Ce sont précisément ces catégories que nous examinons en priorité lors d'un vide maison ou d'un débarras.",
      items: [
        "Mobilier ancien et meubles de style",
        "Bijoux, montres et pièces de joaillerie",
        "Argenterie, porcelaine et vaisselle ancienne",
        "Tableaux, sculptures et objets d'art",
        "Livres, vinyles et collections diverses",
        "Horloges, pendules et luminaires anciens",
      ],
      p2Before: "D'autres pièces peuvent aussi avoir une valeur selon leur état et leur rareté. La liste complète des catégories, avec des exemples détaillés, se trouve sur notre page ",
      p2Link1: "rachat d'objets de valeur",
      p2Mid: ". En cas de doute sur un objet précis, le mieux reste de le montrer plutôt que de trancher seul — voir aussi notre article ",
      p2Link2: "comment reconnaître un objet qui vaut la peine d'être racheté",
      p2After: ".",
    },
    s2: {
      title: "Le rachat, étape par étape",
      steps: [
        ["Premier contact", "Vous décrivez la situation par téléphone ou via le formulaire, avec des photos si possible : elles aident à préparer la visite, mais ne remplacent pas l'examen sur place."],
        ["Examen des objets", "Lors de la visite pour le devis, nous identifions ce qui a une valeur réelle, en même temps que nous évaluons le volume général à débarrasser."],
        ["Estimation transparente", "Nous vous expliquons ce qui est proposé au rachat et sur quelle base — état, matière, ancienneté, demande actuelle — sans jargon ni chiffre imposé."],
        ["Accord objet par objet", "Vous décidez ce que vous acceptez de céder. Rien n'est retiré sans votre validation, y compris pour un objet déjà estimé."],
        ["Déduction sur le devis", "Le montant retenu est soustrait directement du prix du débarras ou du vide maison, et détaillé dans le devis écrit que vous recevez."],
      ],
    },
    box1: {
      title: "Ce processus s'applique à tout type d'intervention",
      before: "Que ce soit dans le cadre d'un ",
      link1: "débarras de maison",
      mid1: ", d'un ",
      link2: "débarras d'appartement",
      mid2: " ou d'un ",
      link3: "débarras de cave, grenier ou garage",
      after: ", l'examen des objets suit toujours la même logique : il fait partie de la visite, pas d'une démarche à part.",
    },
    s3: {
      title: "Le rachat dans le cadre d'une succession",
      p1: "C'est souvent dans ce contexte que le rachat prend tout son sens. Vider le logement d'un proche fait remonter des meubles, bijoux ou objets accumulés sur plusieurs décennies, parfois sans que les héritiers en connaissent la valeur réelle. Plutôt que d'évacuer ces pièces par précaution, ou à l'inverse de les conserver sans savoir qu'en faire, une estimation permet d'objectiver la situation avant de trancher.",
      p2Before: "Cette étape peut aussi faciliter le partage entre héritiers : un meuble ou un objet dont personne ne veut, mais qui a une valeur réelle, peut être racheté plutôt que de rester source de désaccord. Notre article ",
      p2Link1: "partager les meubles entre héritiers",
      p2Mid: " aborde cette question, tandis que notre guide ",
      p2Link2: "vide maison succession à Bruxelles",
      p2After: " détaille l'ensemble du processus, du tri jusqu'à la remise du bien.",
    },
    s4: {
      title: "Comment la valeur d'un objet est-elle déterminée ?",
      p1: "Il n'existe pas de barème fixe applicable à toutes les pièces d'une même catégorie : deux meubles d'apparence proche peuvent recevoir des estimations très différentes. Plusieurs éléments entrent en compte lors de l'examen.",
      items: [
        "L'état général : un objet abîmé ou incomplet perd de la valeur, même s'il est ancien",
        "La matière : bois massif, argent, laiton ou cristal pèsent davantage qu'un matériau courant",
        "Les marques et signatures : un cachet, une griffe ou une signature identifiable change souvent l'estimation",
        "La demande actuelle pour ce type de pièce, qui évolue avec le temps",
        "L'ensemble plutôt que la pièce isolée : une collection complète a souvent plus d'intérêt que des éléments dépareillés",
      ],
      p2: "C'est pourquoi nous ne communiquons pas de fourchette de prix générique par catégorie d'objet avant la visite : l'estimation se fait au cas par cas, sur la base de la pièce réellement examinée.",
    },
    s5: {
      title: "Une estimation honnête, avec ses limites",
      p1: "Nous ne sommes pas experts en art ou en numismatique : pour un objet dont la valeur potentielle dépasse largement le cadre habituel d'un vide maison, il peut arriver que nous recommandions un avis complémentaire plutôt que de proposer un rachat sur une base incertaine. C'est une situation rare, mais nous préférons le dire clairement plutôt que d'avancer un chiffre qui ne reposerait sur rien de solide.",
      p2: "À l'inverse, nous ne sous-évaluons pas non plus une pièce pour réduire artificiellement le montant du rachat : l'objectif reste une estimation qui reflète la réalité au moment de l'intervention, pas un chiffre arrondi à notre avantage. Si un objet ne trouve finalement pas preneur au rachat, cela vous est expliqué avant que quoi que ce soit ne soit décidé.",
    },
    s6: {
      title: "Le rachat ne retarde pas votre vide maison",
      p1: "L'examen des objets ne constitue pas une étape séparée avec sa propre organisation : il s'intègre dans le déroulement normal de la visite et, le jour de l'intervention, dans le tri qui précède l'évacuation. Les objets retenus pour le rachat sont simplement mis de côté ; le reste suit le circuit habituel du débarras.",
      p2Before: "Cette organisation évite un passage supplémentaire chez vous et permet d'avoir, dès le devis, une vision complète du coût final — rachat déduit inclus. Notre page ",
      p2Link: "tarifs",
      p2After: " détaille les autres facteurs qui influencent le prix d'un débarras à Bruxelles.",
    },
    s7: {
      title: "Et les objets qui ne sont pas rachetés ?",
      p1Before: "Une grande partie du contenu d'un bien n'a pas de valeur de revente significative — électroménager ancien, mobilier en kit, vaisselle du quotidien — sans que cela ne signifie qu'il finit automatiquement en déchet. Ces objets sont pris en charge normalement, avec tri par matériau, réemploi ou recyclage selon leur état. Le détail de ce circuit est expliqué dans notre article ",
      p1Link: "que deviennent vos meubles après un débarras",
      p1After: ". Le rachat n'est donc qu'une des issues possibles pour le contenu d'un bien, pas la seule alternative à la mise en déchet.",
    },
    s8: {
      title: "Bien préparer vos objets avant notre passage",
      p1: "Aucune préparation n'est obligatoire : nous nous adaptons à la situation, même si rien n'a été trié au préalable. Quelques gestes simples facilitent toutefois l'examen le jour de la visite.",
      items: [
        "Regrouper si possible les objets potentiellement de valeur dans une même pièce",
        "Conserver les certificats, factures ou boîtes d'origine si vous les avez encore",
        "Éviter de nettoyer ou restaurer un objet ancien avant l'estimation",
        "Prévoir un accès dégagé aux pièces concernées (cave, grenier, dépendances) le jour de la visite",
      ],
    },
    s9: {
      title: "Un examen adapté aux réalités bruxelloises",
      p1: "Une bonne partie du bâti à Bruxelles est faite de maisons de maître ou de mitoyennes avec cave, étages et parfois grenier accessible par une trappe. Un objet fragile — vaisselle ancienne, cadre, pièce d'argenterie — n'est pas manipulé de la même façon selon qu'il se trouve au rez-de-chaussée ou tout en haut d'un escalier étroit. Nous en tenons compte pendant l'examen, sans que cela ne change la méthode d'estimation elle-même.",
      p2: "Si le bien est difficile d'accès — absence de stationnement à proximité, escalier encombré, plusieurs étages sans ascenseur — signalez-le lors du premier contact : cela nous permet de nous organiser en amont, sans que cela ne remette en cause la visite ni l'estimation prévue.",
    },
    aRetenir: {
      title: "À retenir",
      items: [
        "L'examen des objets est gratuit et n'est jamais une condition pour obtenir un devis.",
        "Il se fait lors de la visite prévue pour le débarras ou le vide maison, sans passage supplémentaire.",
        "Vous restez libre d'accepter ou de refuser le rachat, objet par objet.",
        "Le montant retenu est déduit directement du devis, de façon détaillée et vérifiable.",
        "Les objets non rachetés suivent normalement le circuit du débarras : tri, réemploi ou recyclage.",
      ],
    },
    faqTitle: "Questions fréquentes",
    faq: [
      { q: "Le rachat d'objets a-t-il un coût ou une condition à remplir ?", a: "Non. L'examen des objets et leur estimation ne sont jamais facturés séparément, et le rachat n'est jamais une condition pour obtenir un devis de débarras ou de vide maison. Si rien ne vaut la peine d'être racheté, l'intervention se déroule normalement, sans surcoût ni justification à fournir de votre part." },
      { q: "Pouvez-vous me donner un prix avant de voir l'objet ?", a: "Difficilement de façon fiable. Des photos nettes permettent un premier avis, mais la valeur réelle d'un meuble, d'un bijou ou d'une pièce d'argenterie dépend souvent de détails visibles uniquement sur place : état exact, matière, marques ou signatures. L'estimation définitive se confirme donc lors de la visite." },
      { q: "Le rachat allonge-t-il la durée de l'intervention ?", a: "Non, il ne s'agit pas d'un passage supplémentaire. L'examen des objets se fait au même moment que l'évaluation générale du bien, avant le début de l'évacuation. Cela ne modifie pas l'organisation prévue pour le débarras ou le vide maison lui-même." },
      { q: "Puis-je refuser de céder un objet auquel je tiens ?", a: "Oui, sans aucune restriction. Le rachat porte uniquement sur ce que vous acceptez explicitement de céder. Vous pouvez très bien accepter le rachat de certains meubles et conserver un bijou ou un objet sentimental, même si nous lui avons attribué une valeur lors de l'examen." },
      { q: "Que se passe-t-il si aucun objet n'a de valeur de rachat ?", a: "C'est fréquent, et cela n'a aucune incidence sur le reste de l'intervention. Les objets suivent alors le circuit habituel du débarras — évacuation, tri par matériau ou don selon leur état — sans que cela ne retarde ni ne complique le déroulement prévu." },
      { q: "Faut-il nettoyer ou réparer un objet avant votre passage ?", a: "Non, mieux vaut s'en abstenir. Un nettoyage mal maîtrisé ou une réparation improvisée peut parfois réduire la valeur d'une pièce ancienne plutôt que l'augmenter. Il suffit de regrouper les objets concernés dans une même pièce si possible, pour faciliter l'examen le jour venu." },
      { q: "Le montant racheté apparaît-il clairement sur le devis ?", a: "Oui. Le montant retenu pour les objets rachetés est indiqué en déduction du prix global, dans le devis communiqué avant toute intervention. Vous pouvez donc vérifier précisément comment ce montant réduit le coût final du débarras ou du vide maison." },
      { q: "Le rachat fonctionne-t-il aussi lors d'un vide maison de succession ?", a: "Oui, c'est même l'un des cas les plus fréquents. L'estimation peut avoir lieu avant que toutes les démarches soient finalisées, et aider les héritiers à décider ensemble du sort des meubles et objets dont personne ne sait précisément quoi faire, plutôt que de les évacuer par défaut." },
    ],
    ctaFinal: {
      title: "Faites estimer vos objets avant le débarras",
      text: "Décrivez-nous votre situation par téléphone ou via le formulaire : nous vous indiquons si le rachat est pertinent et comment il s'intègre à votre devis gratuit.",
      seeZones: "Voir nos zones d'intervention à Bruxelles",
    },
  },
  nl: {
    h1: "Hoe verloopt de inkoop van voorwerpen bij een huis leegmaken in Brussel?",
    heroText:
      "Bij het leegmaken van een huis in Brussel kunnen bepaalde meubels, juwelen of oude voorwerpen een reële waarde hebben: in plaats van ze samen met de rest af te voeren, onderzoeken we ze en trekken we het bedrag rechtstreeks af van uw offerte. Hier leest u concreet hoe deze schatting verloopt, van het eerste contact tot de uiteindelijke aftrek, zonder aparte stap of verplichting om iets te aanvaarden.",
    callNow: "Bel nu",
    ctaEstimate: "Laat mijn voorwerpen schatten",
    freeQuote: "Vraag een gratis offerte aan",
    s1: {
      title: "Welke voorwerpen komen in aanmerking voor inkoop?",
      p1: "Een pand dat al meerdere decennia bewoond is, bevat vaak stukken waar niemand nog aan denkt om ze te valoriseren: een secretaire opgeborgen in een logeerkamer, een lade vol juwelen, een verzameling boeken of vinylplaten die nooit opnieuw werd beoordeeld. Het zijn precies deze categorieën die we bij voorrang onderzoeken tijdens een huis leegmaken of een ontruiming.",
      items: [
        "Antiek en stijlmeubilair",
        "Juwelen, horloges en sieraden",
        "Zilverwerk, porselein en antiek servies",
        "Schilderijen, beeldhouwwerken en kunstvoorwerpen",
        "Boeken, vinylplaten en diverse verzamelingen",
        "Klokken, pendules en oude verlichting",
      ],
      p2Before: "Andere stukken kunnen ook waarde hebben, afhankelijk van hun staat en zeldzaamheid. De volledige lijst van categorieën, met gedetailleerde voorbeelden, vindt u op onze pagina ",
      p2Link1: "inkoop van waardevolle voorwerpen",
      p2Mid: ". Twijfelt u over een specifiek voorwerp, dan is het beste om het te tonen in plaats van zelf te beslissen — zie ook ons artikel ",
      p2Link2: "hoe herkent u een voorwerp dat de moeite waard is om in te kopen",
      p2After: ".",
    },
    s2: {
      title: "De inkoop, stap voor stap",
      steps: [
        ["Eerste contact", "U beschrijft de situatie telefonisch of via het formulier, met foto's indien mogelijk: die helpen om het bezoek voor te bereiden, maar vervangen het onderzoek ter plaatse niet."],
        ["Onderzoek van de voorwerpen", "Tijdens het bezoek voor de offerte identificeren we wat een reële waarde heeft, tegelijk met de algemene beoordeling van het te ontruimen volume."],
        ["Transparante schatting", "We leggen u uit wat voor inkoop wordt voorgesteld en op welke basis — staat, materiaal, ouderdom, huidige vraag — zonder vakjargon of opgelegd cijfer."],
        ["Akkoord per voorwerp", "U beslist wat u bereid bent af te staan. Niets wordt weggenomen zonder uw goedkeuring, ook niet voor een voorwerp dat al geschat is."],
        ["Aftrek op de offerte", "Het toegekende bedrag wordt rechtstreeks afgetrokken van de prijs van de ontruiming of het leegmaken, en gedetailleerd vermeld in de schriftelijke offerte die u ontvangt."],
      ],
    },
    box1: {
      title: "Dit proces geldt voor elk type interventie",
      before: "Of het nu gaat om een ",
      link1: "huis leegmaken",
      mid1: ", een ",
      link2: "appartement ontruimen",
      mid2: " of een ",
      link3: "kelder, zolder of garage ontruimen",
      after: ", het onderzoek van de voorwerpen volgt altijd dezelfde logica: het maakt deel uit van het bezoek, niet van een aparte stap.",
    },
    s3: {
      title: "De inkoop in het kader van een erfenis",
      p1: "Het is vaak in deze context dat de inkoop zijn volle betekenis krijgt. Het leegmaken van de woning van een dierbare brengt meubels, juwelen of voorwerpen naar boven die over meerdere decennia zijn opgestapeld, soms zonder dat de erfgenamen de werkelijke waarde ervan kennen. In plaats van deze stukken uit voorzorg af te voeren, of ze omgekeerd te bewaren zonder te weten wat ermee te doen, maakt een schatting het mogelijk om de situatie objectief te bekijken vóór er beslist wordt.",
      p2Before: "Deze stap kan ook de verdeling tussen erfgenamen vergemakkelijken: een meubelstuk of voorwerp waar niemand iets mee wil, maar dat wel een reële waarde heeft, kan worden ingekocht in plaats van bron van onenigheid te blijven. Ons artikel ",
      p2Link1: "meubels verdelen tussen erfgenamen",
      p2Mid: " behandelt deze vraag, terwijl onze gids ",
      p2Link2: "huis leegmaken bij een erfenis in Brussel",
      p2After: " het volledige proces beschrijft, van de sortering tot de oplevering van het pand.",
    },
    s4: {
      title: "Hoe wordt de waarde van een voorwerp bepaald?",
      p1: "Er bestaat geen vast tarief dat van toepassing is op alle stukken uit eenzelfde categorie: twee meubels met een vergelijkbaar uiterlijk kunnen zeer verschillende schattingen krijgen. Verschillende elementen spelen mee tijdens het onderzoek.",
      items: [
        "De algemene staat: een beschadigd of onvolledig voorwerp verliest waarde, ook al is het oud",
        "Het materiaal: massief hout, zilver, messing of kristal wegen zwaarder door dan een gewoon materiaal",
        "Merken en signaturen: een stempel, een merkteken of een herkenbare signatuur wijzigt vaak de schatting",
        "De huidige vraag naar dit type stuk, die evolueert met de tijd",
        "Het geheel eerder dan het losse stuk: een complete verzameling is vaak interessanter dan losse, niet-passende elementen",
      ],
      p2: "Daarom communiceren we vóór het bezoek geen generieke prijsrange per objectcategorie: de schatting gebeurt geval per geval, op basis van het werkelijk onderzochte stuk.",
    },
    s5: {
      title: "Een eerlijke schatting, met haar grenzen",
      p1: "Wij zijn geen experts in kunst of numismatiek: voor een voorwerp waarvan de potentiële waarde ruim boven het gebruikelijke kader van een huis leegmaken uitstijgt, kan het gebeuren dat we een bijkomend advies aanraden in plaats van een inkoop op onzekere basis voor te stellen. Dit is een zeldzame situatie, maar we zeggen dit liever duidelijk dan een cijfer voor te stellen dat nergens op gebaseerd is.",
      p2: "Omgekeerd onderschatten we een stuk ook niet om het inkoopbedrag kunstmatig te verlagen: het doel blijft een schatting die de werkelijkheid op het moment van de interventie weerspiegelt, geen naar beneden afgerond cijfer in ons voordeel. Als een voorwerp uiteindelijk geen koper vindt voor inkoop, wordt u dat uitgelegd voordat er iets beslist wordt.",
    },
    s6: {
      title: "De inkoop vertraagt uw huis leegmaken niet",
      p1: "Het onderzoek van de voorwerpen vormt geen aparte stap met een eigen organisatie: het is geïntegreerd in het normale verloop van het bezoek en, op de dag van de interventie, in de sortering die aan de afvoer voorafgaat. De voor inkoop weerhouden voorwerpen worden gewoon apart gehouden; de rest volgt het gebruikelijke traject van de ontruiming.",
      p2Before: "Deze organisatie vermijdt een extra bezoek bij u thuis en zorgt ervoor dat u al bij de offerte een volledig zicht heeft op de eindkost — inkoop inbegrepen en afgetrokken. Onze pagina ",
      p2Link: "tarieven",
      p2After: " licht de andere factoren toe die de prijs van een ontruiming in Brussel beïnvloeden.",
    },
    s7: {
      title: "En de voorwerpen die niet worden ingekocht?",
      p1Before: "Een groot deel van de inhoud van een pand heeft geen aanzienlijke wederverkoopwaarde — oude huishoudtoestellen, meubels in zelfbouwpakket, alledaags servies — zonder dat dit betekent dat het automatisch als afval eindigt. Deze voorwerpen worden normaal verwerkt, met sortering per materiaal, hergebruik of recyclage naargelang hun staat. Het verloop van dit traject wordt uitgelegd in ons artikel ",
      p1Link: "wat gebeurt er met uw meubels na een ontruiming",
      p1After: ". Inkoop is dus slechts een van de mogelijke bestemmingen voor de inhoud van een pand, niet het enige alternatief voor afvoer als afval.",
    },
    s8: {
      title: "Bereid uw voorwerpen goed voor vóór onze komst",
      p1: "Geen enkele voorbereiding is verplicht: we passen ons aan de situatie aan, ook als er vooraf niets werd gesorteerd. Enkele eenvoudige gebaren vergemakkelijken echter het onderzoek op de dag van het bezoek.",
      items: [
        "Groepeer indien mogelijk de mogelijk waardevolle voorwerpen in dezelfde ruimte",
        "Bewaar de certificaten, facturen of originele dozen als u die nog heeft",
        "Vermijd het reinigen of restaureren van een oud voorwerp vóór de schatting",
        "Voorzie een vrije toegang tot de betrokken ruimtes (kelder, zolder, bijgebouwen) op de dag van het bezoek",
      ],
    },
    s9: {
      title: "Een onderzoek aangepast aan de Brusselse realiteit",
      p1: "Een groot deel van het Brusselse bouwpatrimonium bestaat uit herenhuizen of rijwoningen met een kelder, verdiepingen en soms een zolder die via een luik bereikbaar is. Een breekbaar voorwerp — antiek servies, een lijst, een stuk zilverwerk — wordt niet op dezelfde manier behandeld naargelang het zich op het gelijkvloers bevindt of helemaal boven aan een smalle trap. We houden hier rekening mee tijdens het onderzoek, zonder dat dit de schattingsmethode zelf verandert.",
      p2: "Als het pand moeilijk toegankelijk is — geen parkeergelegenheid in de buurt, een volle trap, meerdere verdiepingen zonder lift — meld dit dan bij het eerste contact: zo kunnen we ons vooraf organiseren, zonder dat dit het geplande bezoek of de schatting in vraag stelt.",
    },
    aRetenir: {
      title: "Om te onthouden",
      items: [
        "Het onderzoek van de voorwerpen is gratis en nooit een voorwaarde om een offerte te krijgen.",
        "Het gebeurt tijdens het bezoek voorzien voor de ontruiming of het leegmaken, zonder extra bezoek.",
        "U blijft vrij om de inkoop te aanvaarden of te weigeren, per voorwerp.",
        "Het toegekende bedrag wordt rechtstreeks afgetrokken van de offerte, op een gedetailleerde en controleerbare manier.",
        "Niet-ingekochte voorwerpen volgen normaal het traject van de ontruiming: sortering, hergebruik of recyclage.",
      ],
    },
    faqTitle: "Veelgestelde vragen",
    faq: [
      { q: "Heeft de inkoop van voorwerpen kosten of een voorwaarde?", a: "Nee. Het onderzoek van de voorwerpen en hun schatting worden nooit apart gefactureerd, en inkoop is nooit een voorwaarde om een offerte voor ontruiming of huis leegmaken te krijgen. Als niets de moeite waard is om in te kopen, verloopt de interventie normaal, zonder meerkost of verantwoording van uw kant." },
      { q: "Kunt u me een prijs geven vóór u het voorwerp ziet?", a: "Dat is moeilijk om betrouwbaar te doen. Duidelijke foto's laten een eerste inschatting toe, maar de werkelijke waarde van een meubelstuk, juweel of stuk zilverwerk hangt vaak af van details die enkel ter plaatse zichtbaar zijn: precieze staat, materiaal, merktekens of signaturen. De definitieve schatting wordt dus bevestigd tijdens het bezoek." },
      { q: "Verlengt de inkoop de duur van de interventie?", a: "Nee, het gaat niet om een extra bezoek. Het onderzoek van de voorwerpen gebeurt op hetzelfde moment als de algemene beoordeling van het pand, vóór het begin van de afvoer. Dit verandert niets aan de geplande organisatie van de ontruiming of het huis leegmaken zelf." },
      { q: "Kan ik weigeren een voorwerp waaraan ik gehecht ben af te staan?", a: "Ja, zonder enige beperking. De inkoop betreft alleen wat u expliciet aanvaardt af te staan. U kunt heel goed instemmen met de inkoop van bepaalde meubels en een juweel of sentimenteel voorwerp behouden, ook al hebben we er tijdens het onderzoek een waarde aan toegekend." },
      { q: "Wat gebeurt er als geen enkel voorwerp inkoopwaarde heeft?", a: "Dat komt vaak voor en heeft geen enkele invloed op de rest van de interventie. De voorwerpen volgen dan het gebruikelijke traject van de ontruiming — afvoer, sortering per materiaal of schenking naargelang hun staat — zonder dat dit het geplande verloop vertraagt of bemoeilijkt." },
      { q: "Moet ik een voorwerp reinigen of herstellen vóór uw komst?", a: "Nee, dat kunt u beter niet doen. Een slecht uitgevoerde reiniging of een geïmproviseerde herstelling kan de waarde van een antiek stuk soms verlagen in plaats van verhogen. Het volstaat om de betrokken voorwerpen indien mogelijk in dezelfde ruimte te groeperen, om het onderzoek op de dag zelf te vergemakkelijken." },
      { q: "Verschijnt het ingekochte bedrag duidelijk op de offerte?", a: "Ja. Het bedrag dat voor de ingekochte voorwerpen wordt weerhouden, wordt vermeld als aftrek van de totale prijs, in de offerte die u vóór elke interventie ontvangt. U kunt dus precies nagaan hoe dit bedrag de eindkost van de ontruiming of het leegmaken verlaagt." },
      { q: "Werkt de inkoop ook bij een huis leegmaken naar aanleiding van een erfenis?", a: "Ja, dit is zelfs een van de meest voorkomende gevallen. De schatting kan plaatsvinden vóór alle formaliteiten zijn afgerond, en helpt de erfgenamen om samen te beslissen over het lot van meubels en voorwerpen waarvan niemand precies weet wat ermee te doen, in plaats van ze standaard af te voeren." },
    ],
    ctaFinal: {
      title: "Laat uw voorwerpen schatten vóór de ontruiming",
      text: "Beschrijf ons uw situatie telefonisch of via het formulier: we geven aan of inkoop relevant is en hoe die past binnen uw gratis offerte.",
      seeZones: "Bekijk onze interventiezones in Brussel",
    },
  },
  en: {
    h1: "How does item buyback work during a house clearance in Brussels?",
    heroText:
      "During a house clearance in Brussels, certain furniture, jewellery or antique items may have real value: rather than removing them with the rest, we assess them and deduct their value directly from your quote. Here is exactly how this appraisal works, from first contact to the final deduction, with no separate process or obligation to accept anything.",
    callNow: "Call now",
    ctaEstimate: "Get my items appraised",
    freeQuote: "Request a free quote",
    s1: {
      title: "Which items are eligible for buyback?",
      p1: "A property occupied for several decades often contains pieces no one thinks to value any more: a writing desk tucked away in a spare room, a drawer full of jewellery, a collection of books or vinyl records never reassessed. These are precisely the categories we examine as a priority during a house clearance or removal.",
      items: [
        "Antique and period furniture",
        "Jewellery, watches and fine jewellery pieces",
        "Silverware, porcelain and antique tableware",
        "Paintings, sculptures and art objects",
        "Books, vinyl records and various collections",
        "Clocks, mantel clocks and antique lighting",
      ],
      p2Before: "Other pieces may also have value depending on their condition and rarity. The full list of categories, with detailed examples, is available on our ",
      p2Link1: "item buyback",
      p2Mid: " page. If in doubt about a specific item, it's best to show it to us rather than decide alone — see also our article on ",
      p2Link2: "how to recognise an item worth buying back",
      p2After: ".",
    },
    s2: {
      title: "Buyback, step by step",
      steps: [
        ["First contact", "You describe the situation by phone or via the form, with photos if possible: they help prepare the visit but do not replace an on-site examination."],
        ["Examining the items", "During the quote visit, we identify what has real value at the same time as we assess the overall volume to be cleared."],
        ["Transparent appraisal", "We explain what is being offered for buyback and on what basis — condition, material, age, current demand — with no jargon or imposed figures."],
        ["Item-by-item agreement", "You decide what you agree to give up. Nothing is removed without your approval, even for an item already appraised."],
        ["Deduction from the quote", "The agreed amount is deducted directly from the price of the clearance or house clearance, and detailed in the written quote you receive."],
      ],
    },
    box1: {
      title: "This process applies to every type of job",
      before: "Whether as part of a ",
      link1: "house clearance",
      mid1: ", an ",
      link2: "apartment clearance",
      mid2: " or a ",
      link3: "cellar, attic or garage clearance",
      after: ", the examination of items always follows the same logic: it is part of the visit, not a separate process.",
    },
    s3: {
      title: "Buyback in the context of an estate",
      p1: "It is often in this context that buyback makes the most sense. Clearing a loved one's home brings up furniture, jewellery or items accumulated over several decades, sometimes without the heirs knowing their real value. Rather than removing these pieces as a precaution, or conversely keeping them without knowing what to do with them, an appraisal makes it possible to look at the situation objectively before deciding.",
      p2Before: "This step can also make it easier to share items between heirs: a piece of furniture or item no one wants, but which has real value, can be bought back rather than remain a source of disagreement. Our article on ",
      p2Link1: "sharing furniture between heirs",
      p2Mid: " covers this question, while our guide to ",
      p2Link2: "house clearance for an estate in Brussels",
      p2After: " details the entire process, from sorting through to handing over the property.",
    },
    s4: {
      title: "How is the value of an item determined?",
      p1: "There is no fixed scale that applies to all pieces within the same category: two similar-looking pieces of furniture can receive very different appraisals. Several factors come into play during the examination.",
      items: [
        "General condition: a damaged or incomplete item loses value, even if it is old",
        "Material: solid wood, silver, brass or crystal carry more weight than an ordinary material",
        "Marks and signatures: a hallmark, a maker's mark or an identifiable signature often changes the appraisal",
        "Current demand for this type of piece, which changes over time",
        "The set rather than the isolated piece: a complete collection is often more interesting than mismatched items",
      ],
      p2: "This is why we do not give a generic price range by item category before the visit: the appraisal is done case by case, based on the piece actually examined.",
    },
    s5: {
      title: "An honest appraisal, with its limits",
      p1: "We are not experts in art or numismatics: for an item whose potential value goes well beyond the usual scope of a house clearance, we may recommend a further opinion rather than offer a buyback on an uncertain basis. This is a rare situation, but we prefer to say so clearly rather than put forward a figure with no solid basis.",
      p2: "Conversely, we do not undervalue a piece to artificially lower the buyback amount either: the goal remains an appraisal that reflects reality at the time of the job, not a figure rounded in our favour. If an item ultimately finds no buyer, this is explained to you before anything is decided.",
    },
    s6: {
      title: "Buyback does not delay your house clearance",
      p1: "Examining items is not a separate step with its own organisation: it is built into the normal course of the visit and, on the day of the job, into the sorting that precedes removal. Items selected for buyback are simply set aside; the rest follows the usual clearance process.",
      p2Before: "This organisation avoids an extra visit to your home and means you get a complete view of the final cost from the quote stage onward — buyback deduction included. Our ",
      p2Link: "prices",
      p2After: " page details the other factors that influence the cost of a clearance in Brussels.",
    },
    s7: {
      title: "And the items that are not bought back?",
      p1Before: "A large part of a property's contents has no significant resale value — old appliances, flat-pack furniture, everyday tableware — without this meaning it automatically ends up as waste. These items are handled normally, sorted by material, reused or recycled depending on their condition. This process is detailed in our article on ",
      p1Link: "what happens to your furniture after a clearance",
      p1After: ". Buyback is therefore just one of several possible outcomes for a property's contents, not the only alternative to disposal.",
    },
    s8: {
      title: "Preparing your items before we arrive",
      p1: "No preparation is required: we adapt to the situation, even if nothing has been sorted beforehand. A few simple steps nonetheless make the examination easier on the day of the visit.",
      items: [
        "Group potentially valuable items together in the same room, if possible",
        "Keep any certificates, invoices or original boxes if you still have them",
        "Avoid cleaning or restoring an antique item before the appraisal",
        "Provide clear access to the relevant rooms (cellar, attic, outbuildings) on the day of the visit",
      ],
    },
    s9: {
      title: "An examination adapted to Brussels' realities",
      p1: "A good part of Brussels' housing stock consists of townhouses or terraced houses with a cellar, several floors and sometimes an attic accessible via a hatch. A fragile item — antique tableware, a picture frame, a piece of silverware — is not handled the same way whether it is on the ground floor or right at the top of a narrow staircase. We take this into account during the examination, without it changing the appraisal method itself.",
      p2: "If the property is hard to access — no nearby parking, a cluttered staircase, several floors with no lift — let us know at first contact: this allows us to plan ahead, without calling the visit or the planned appraisal into question.",
    },
    aRetenir: {
      title: "Key takeaways",
      items: [
        "Examining items is free and never a condition for getting a quote.",
        "It takes place during the visit planned for the clearance or house clearance, with no extra visit.",
        "You remain free to accept or decline the buyback, item by item.",
        "The agreed amount is deducted directly from the quote, in a detailed and verifiable way.",
        "Items not bought back follow the normal clearance process: sorting, reuse or recycling.",
      ],
    },
    faqTitle: "Frequently asked questions",
    faq: [
      { q: "Does item buyback come with a cost or a condition to meet?", a: "No. Examining items and appraising them is never billed separately, and buyback is never a condition for getting a clearance or house clearance quote. If nothing is worth buying back, the job proceeds as normal, with no extra cost and nothing to justify on your part." },
      { q: "Can you give me a price before seeing the item?", a: "That is difficult to do reliably. Clear photos allow for an initial opinion, but the real value of a piece of furniture, a piece of jewellery or a piece of silverware often depends on details only visible on site: exact condition, material, marks or signatures. The final appraisal is therefore confirmed during the visit." },
      { q: "Does buyback lengthen the job?", a: "No, it is not an additional visit. Examining items happens at the same time as the general assessment of the property, before removal begins. This does not change the planned organisation of the clearance or house clearance itself." },
      { q: "Can I refuse to give up an item I am attached to?", a: "Yes, without any restriction. Buyback only covers what you explicitly agree to give up. You can very well accept the buyback of certain furniture and keep a piece of jewellery or a sentimental item, even if we assigned it a value during the examination." },
      { q: "What happens if no item has buyback value?", a: "This is common, and it has no impact on the rest of the job. Items then follow the usual clearance process — removal, sorting by material or donation depending on condition — without this delaying or complicating the planned course of action." },
      { q: "Should I clean or repair an item before your visit?", a: "No, it is best to avoid it. A poorly done cleaning job or an improvised repair can sometimes lower the value of an antique piece rather than increase it. It is enough to group the relevant items together in one room, if possible, to make the examination easier on the day." },
      { q: "Does the buyback amount appear clearly on the quote?", a: "Yes. The amount agreed for the bought-back items is shown as a deduction from the overall price, in the quote provided before any work begins. You can therefore check exactly how this amount reduces the final cost of the clearance or house clearance." },
      { q: "Does buyback also apply during a house clearance for an estate?", a: "Yes, this is in fact one of the most common cases. The appraisal can take place before all the formalities are finalised, and can help heirs decide together on the fate of furniture and items that no one quite knows what to do with, rather than removing them by default." },
    ],
    ctaFinal: {
      title: "Get your items appraised before the clearance",
      text: "Tell us about your situation by phone or via the form: we will let you know whether buyback applies and how it fits into your free quote.",
      seeZones: "See our service areas in Brussels",
    },
  },
};

const faqJsonLdFor = (faq) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
});

export default function PageClient({ locale }) {
  const c = CONTENT[locale];
  const faqJsonLd = faqJsonLdFor(c.faq);

  return (
    <article>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />

      <section className="relative overflow-hidden bg-sand text-ink">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(900px 480px at 85% -10%, rgba(63,108,81,0.08), transparent 55%)" }} />
        <div className="relative mx-auto max-w-[900px] px-6 py-20 lg:px-10 lg:py-24">
          <h1 className="max-w-3xl font-serif text-4xl font-black leading-[1.1] md:text-5xl">
            {c.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-ink/70 leading-relaxed">
            {c.heroText}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 rounded-2xl bg-cta-700 px-7 py-3.5 font-semibold text-white shadow-cta transition hover:-translate-y-0.5 hover:bg-cta-800">
              <PhoneIcon className="h-5 w-5" />
              {c.callNow}
            </a>
            <a href="#contactus" className="inline-flex items-center gap-2 rounded-2xl border-2 border-ink/15 bg-white px-7 py-3.5 font-semibold text-ink transition hover:border-accent hover:text-accent">
              {c.ctaEstimate}
              <ArrowRightIcon className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-[800px] px-6 lg:px-10 space-y-8">
          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s1.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s1.p1}
            </p>
            <ul className="mt-4 space-y-2">
              {c.s1.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sage" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s1.p2Before}
              <Link href="/rachat-objets" className="font-semibold text-accent underline underline-offset-4">
                {c.s1.p2Link1}
              </Link>
              {c.s1.p2Mid}
              <Link href="/blog/reconnaitre-objet-valeur-rachat" className="font-semibold text-accent underline underline-offset-4">
                {c.s1.p2Link2}
              </Link>
              {c.s1.p2After}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s2.title}</h2>
            <ol className="mt-4 space-y-4">
              {c.s2.steps.map(([title, text], i) => (
                <li key={title} className="flex gap-4 rounded-2xl border border-sand-line bg-sand p-5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink font-serif font-bold text-white">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-bold text-ink">{title}</p>
                    <p className="mt-1 text-sm text-neutral-600">{text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-3xl border border-sage/25 bg-sage/5 p-6">
            <p className="font-bold text-ink">{c.box1.title}</p>
            <p className="mt-2 text-sm leading-relaxed text-neutral-700">
              {c.box1.before}
              <Link href="/debarras-maison" className="font-semibold text-sage underline decoration-sage/40 underline-offset-2 hover:text-sage-light">
                {c.box1.link1}
              </Link>
              {c.box1.mid1}
              <Link href="/debarras-appartement" className="font-semibold text-sage underline decoration-sage/40 underline-offset-2 hover:text-sage-light">
                {c.box1.link2}
              </Link>
              {c.box1.mid2}
              <Link href="/debarras-cave-grenier-garage" className="font-semibold text-sage underline decoration-sage/40 underline-offset-2 hover:text-sage-light">
                {c.box1.link3}
              </Link>
              {c.box1.after}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s3.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s3.p1}
            </p>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s3.p2Before}
              <Link href="/blog/succession-partager-meubles-heritiers" className="font-semibold text-accent underline underline-offset-4">
                {c.s3.p2Link1}
              </Link>
              {c.s3.p2Mid}
              <Link href="/blog/vide-maison-succession-bruxelles" className="font-semibold text-accent underline underline-offset-4">
                {c.s3.p2Link2}
              </Link>
              {c.s3.p2After}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s4.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s4.p1}
            </p>
            <ul className="mt-4 space-y-2">
              {c.s4.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sage" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s4.p2}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s5.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s5.p1}
            </p>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s5.p2}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s6.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s6.p1}
            </p>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s6.p2Before}
              <Link href="/tarifs" className="font-semibold text-accent underline underline-offset-4">
                {c.s6.p2Link}
              </Link>
              {c.s6.p2After}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s7.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s7.p1Before}
              <Link href="/blog/que-deviennent-meubles-apres-debarras" className="font-semibold text-accent underline underline-offset-4">
                {c.s7.p1Link}
              </Link>
              {c.s7.p1After}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s8.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s8.p1}
            </p>
            <ul className="mt-4 space-y-2">
              {c.s8.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sage" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s9.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s9.p1}
            </p>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s9.p2}
            </p>
          </div>

          <div className="rounded-3xl border border-sand-line bg-sand p-6">
            <p className="font-bold text-ink">{c.aRetenir.title}</p>
            <ul className="mt-3 space-y-2">
              {c.aRetenir.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-neutral-700">
                  <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-sage" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.faqTitle}</h2>
            <div className="mt-4 space-y-4">
              {c.faq.map((item) => (
                <div key={item.q} className="rounded-2xl border border-sand-line bg-sand p-5">
                  <p className="font-bold text-ink">{item.q}</p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-sand-line bg-ink p-8 text-white">
            <h2 className="font-serif text-2xl font-extrabold">{c.ctaFinal.title}</h2>
            <p className="mt-2 text-white/75">
              {c.ctaFinal.text}
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 rounded-2xl bg-cta-700 px-6 py-3 font-semibold text-white shadow-cta transition hover:-translate-y-0.5 hover:bg-cta-800">
                <PhoneIcon className="h-5 w-5" />
                {c.callNow}
              </a>
              <a href="#contactus" className="inline-flex items-center gap-2 rounded-2xl border border-white/25 bg-white/10 px-6 py-3 font-semibold text-white transition hover:bg-white/20">
                <ClipboardDocumentCheckIcon className="h-5 w-5" />
                {c.freeQuote}
              </a>
            </div>
            <div className="mt-6">
              <Link href="/vide-maison" className="inline-flex items-center gap-2 font-bold text-accent-light hover:text-accent">
                {c.ctaFinal.seeZones}
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Contact />
    </article>
  );
}
